import { TypedDocumentNode, gql } from '@apollo/client';
import {
  faBell,
  faBellOn,
  faBellSlash,
} from '@fortawesome/pro-solid-svg-icons';
import classnames from 'classnames';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { IconButton } from 'atoms/buttons/IconButton';
import { Dropdown, DropdownOptionLabel } from 'atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from 'atoms/icons';
import { Checkbox } from 'atoms/inputs/Checkbox';
import { BodyM } from 'atoms/typography';
import { ScarcityBall } from 'components/card/ScarcityBall';
import { Dialog } from 'components/dialog';
import { useUpdateSubscription } from 'hooks/subscriptions/useUpdateSubscription';
import {
  BlockchainScarcity,
  subscribableRarities as so5SubscribableRarities,
  usSportSubscribableRarities,
} from 'lib/cards';
import { type FragmentOf, unmaskFragment } from 'lib/graphql';

import type { NotificationPreference_subscription } from './__generated__/index.graphql';

const bell = (subscribableRarities: string[], notifyForRarities: string[]) => {
  if (notifyForRarities.length === 0) {
    return faBellSlash;
  }
  if (subscribableRarities.every(r => notifyForRarities.includes(r))) {
    return faBellOn;
  }
  return faBell;
};

const messages = defineMessages({
  dialogTitle: {
    id: 'NotificationPreference.dialogTitle',
    defaultMessage: 'Scarcities you want to be notified for',
  },
});

const CustomRoot = styled.div`
  display: flex;
  justify-content: space-between;
`;

type CustomScarcityPreferenceProps = {
  scarcity: BlockchainScarcity;
  enabled: boolean;
  toggle: () => void;
};
const CustomScarcityPreference = ({
  scarcity,
  enabled,
  toggle,
}: CustomScarcityPreferenceProps) => {
  return (
    <CustomRoot>
      <ScarcityBall scarcity={scarcity} />
      <Checkbox checked={enabled} onChange={toggle} />
    </CustomRoot>
  );
};

const Body = styled.div`
  padding: var(--triple-unit);
`;
const DialogContent = styled.div`
  margin-top: var(--double-unit);
  & > * + * {
    margin-top: var(--double-unit);
    border-top: 1px solid var(--c-nd-100);
    padding-top: var(--double-unit);
  }
`;

type Props = {
  subscription: FragmentOf<NotificationPreference_subscription>;
};

const subscribableRaritiesByType: Record<string, BlockchainScarcity[]> = {
  Player: so5SubscribableRarities as BlockchainScarcity[],
  NBAPlayer: usSportSubscribableRarities as BlockchainScarcity[],
  BaseballPlayer: usSportSubscribableRarities as BlockchainScarcity[],
};

export const NotificationPreference = ({
  subscription: subscription_,
}: Props) => {
  const subscription = unmaskFragment(subscription_);
  const [customOpened, setCustomOpened] = useState(false);
  const [open, setOpen] = useState(false);
  const updateSubscription = useUpdateSubscription(subscription);
  const { notifyForRarities } = subscription.preferences;

  const updateSubscriptionRarities = async (
    scarcities: BlockchainScarcity[],
    updatedValue: boolean
  ) => {
    await updateSubscription(scarcities, updatedValue);
  };

  const subscribableRarities =
    subscribableRaritiesByType[subscription.anySubscribable.__typename] || [];

  return (
    <>
      <Dialog
        maxWidth="sm"
        fullWidth
        open={customOpened}
        onClose={() => {
          setCustomOpened(false);
          setOpen(false);
        }}
        title={
          <BodyM bold className="text-center">
            <FormattedMessage {...messages.dialogTitle} />
          </BodyM>
        }
      >
        <Body>
          <BodyM>
            <FormattedMessage
              id="NotificationPreference.help"
              defaultMessage="Each time a new Card is listed you’ll receive a notification"
            />
          </BodyM>
          <DialogContent>
            {subscribableRarities.map(scarcity => {
              const enabled = notifyForRarities.includes(scarcity);
              return (
                <CustomScarcityPreference
                  key={scarcity}
                  scarcity={scarcity}
                  enabled={enabled}
                  toggle={() => {
                    updateSubscriptionRarities([scarcity], !enabled);
                  }}
                />
              );
            })}
          </DialogContent>
        </Body>
      </Dialog>
      <Dropdown
        label={props => (
          <IconButton
            icon={bell(subscribableRarities, notifyForRarities)}
            color="tertiary"
            disableDebounce
            {...props}
          />
        )}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        open={open}
      >
        <DropdownOptionLabel
          className={classnames({
            selected: subscribableRarities.every(r =>
              notifyForRarities.includes(r)
            ),
          })}
          onClick={async () => {
            await updateSubscriptionRarities(subscribableRarities, true);
            setOpen(false);
          }}
        >
          <FontAwesomeIcon icon={faBellOn} />
          <FormattedMessage
            id="NotificationPreference.all"
            defaultMessage="All scarcities"
          />
        </DropdownOptionLabel>
        <DropdownOptionLabel
          className={classnames({
            selected:
              notifyForRarities.length > 0 &&
              notifyForRarities.length < subscribableRarities.length,
          })}
          onClick={() => setCustomOpened(true)}
        >
          <FontAwesomeIcon icon={faBell} />
          <FormattedMessage
            id="NotificationPreference.custom"
            defaultMessage="Custom"
          />
        </DropdownOptionLabel>
        <DropdownOptionLabel
          className={classnames({
            selected: notifyForRarities.length === 0,
          })}
          onClick={() => {
            updateSubscriptionRarities(subscribableRarities, false);
          }}
        >
          <FontAwesomeIcon icon={faBellSlash} />
          <FormattedMessage
            id="NotificationPreference.none"
            defaultMessage="None"
          />
        </DropdownOptionLabel>
      </Dropdown>
    </>
  );
};

NotificationPreference.fragments = {
  subscription: gql`
    fragment NotificationPreference_subscription on EmailSubscription {
      slug
      preferences {
        slug
        notifyForRarities
      }
      anySubscribable {
        slug
      }
    }
  ` as TypedDocumentNode<NotificationPreference_subscription>,
};
